import "core-js/modules/es.array.reverse.js";
var _dec, _class, _descriptor, _descriptor2, _descriptor3;
function _initializerDefineProperty(e, i, r, l) { r && Object.defineProperty(e, i, { enumerable: r.enumerable, configurable: r.configurable, writable: r.writable, value: r.initializer ? r.initializer.call(l) : void 0 }); }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
function _initializerWarningHelper(r, e) { throw Error("Decorating class property failed. Please ensure that transform-class-properties is enabled and runs after the decorators transform."); }
import { action, computed, observable, runInAction, makeObservable } from "mobx";
import Result from "./Result";
import TerriaError from "./TerriaError";

/**
 *
 * The AsyncLoader class provides a way to memoize (of sorts) async requests. In a `forceLoadX` function you should load from an asynchronous service, transform the data into something that can be stored in 1 or multiple observables and then set those observables.
 *
 * It works by calling `loadCallback` in `@computed loadKeepAlive`. This `@computed` will update if observables change that were used in `loadCallback()`.Because we are using a `@computed` in this way - it is **very important** that no changes to `observables` are made **before an async call**.
 *
 * A **correct** example:
 * ```ts
 * async function loadX() {
 *    const url = this.someObservableUrl
 *    const someData = await loadText(url)
 *    runInAction(() => this.someOtherObservable = someData)
 * }
 * ```
 *
 * This function will only be called *again* when `someObservableUrl` changes.
 *
 * ------------------------
 *
 * If there is any synchronous processing present it should be pulled out of forceLoadX and placed into 1 or multiple computeds.
 *
 * An **incorrect** example:
 * ```ts
 * async function loadX() {
 *    const arg = this.someObservable
 *    const someData = someSynchronousFn(arg)
 *    runInAction(() => this.someOtherObservable = someData)
 * }
 * ```
 *
 * Instead this should be in a `@computed`:
 *
 * ```ts
 * @computed
 * get newComputed {
 *    return someSynchronousFn(this.someObservable);
 * }
 * ```
 *
 * ------------------------
 *
 * **Other tips**:
 *
 * - You can not nest together `AsyncLoaders`.
 *
 * **Examples**:
 *
 * See:
 * - `MappableMixin`
 * - `CatalogMemberMixin`
 */
let AsyncLoader = (_dec = computed({
  keepAlive: true
}), _class = class AsyncLoader {
  get loadKeepAlive() {
    // We don't do much with _forceReloadCount directly, but by accessing it
    // we will cause a new load to be triggered when it changes. If it's value
    // is -1, we don't call the `loadCallback` at all, so this keepAlive'd
    // observable will no longer depend on anything, which avoids creating
    // a memory leak when this loader is no longer needed.
    if (this._forceReloadCount < 0) {
      if (this.disposeCallback) {
        return this.disposeCallback();
      } else {
        return Promise.resolve();
      }
    }
    return this.loadCallback();
  }
  constructor(/** {@see AsyncLoader} */
  loadCallback, disposeCallback) {
    this.loadCallback = loadCallback;
    this.disposeCallback = disposeCallback;
    _initializerDefineProperty(this, "_isLoading", _descriptor, this);
    _initializerDefineProperty(this, "_result", _descriptor2, this);
    _initializerDefineProperty(this, "_forceReloadCount", _descriptor3, this);
    _defineProperty(this, "_promise", undefined);
    makeObservable(this);
  }

  /**
   * Gets a value indicating whether we are currently loading.
   */
  get isLoading() {
    return this._isLoading;
  }
  get result() {
    return this._result;
  }
  async load() {
    let forceReload = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    if (forceReload) {
      runInAction(() => ++this._forceReloadCount);
    }
    const newPromise = this.loadKeepAlive;
    if (newPromise !== this._promise) {
      if (this._promise) {
        // TODO - cancel old promise
        //this._metadataPromise.cancel();
      }
      this._promise = newPromise;
      runInAction(() => {
        this._isLoading = true;
      });
    }
    let error;
    try {
      await newPromise;
    } catch (e) {
      error = TerriaError.from(e);
    }
    runInAction(() => {
      this._result = Result.none(error);
      this._isLoading = false;
    });
    return this._result;
  }

  /**
   * Disposes this loader, allowing the loaded resources to be garbage collected.
   * The loader can be resurrected and forced to load again by calling
   * `load(true)`.
   */
  dispose() {
    this._forceReloadCount = -1;
    this.load();
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "_isLoading", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "_result", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return undefined;
  }
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "_forceReloadCount", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return 0;
  }
}), _applyDecoratedDescriptor(_class.prototype, "loadKeepAlive", [_dec], Object.getOwnPropertyDescriptor(_class.prototype, "loadKeepAlive"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "load", [action], Object.getOwnPropertyDescriptor(_class.prototype, "load"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "dispose", [action], Object.getOwnPropertyDescriptor(_class.prototype, "dispose"), _class.prototype), _class);
export { AsyncLoader as default };